import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';

// Helper function to format HTML description
const formatDescription = (html) => {
  if (!html) return '';
  
  // Remove <p> tags and replace with line breaks
  let text = html.replace(/<p>/g, '').replace(/<\/p>/g, '\n');
  
  // Convert <strong> to plain text (keep the content)
  text = text.replace(/<strong>/g, '').replace(/<\/strong>/g, '');
  
  // Convert <em> to plain text
  text = text.replace(/<em>/g, '').replace(/<\/em>/g, '');
  
  // Convert <code> to backticks
  text = text.replace(/<code>/g, '`').replace(/<\/code>/g, '`');
  
  // Handle lists - convert <ul><li> to bullet points
  text = text.replace(/<ul>/g, '').replace(/<\/ul>/g, '');
  text = text.replace(/<li>/g, '• ').replace(/<\/li>/g, '\n');
  
  // Handle <ol><li> to numbered lists
  text = text.replace(/<ol>/g, '').replace(/<\/ol>/g, '');
  
  // Remove <br> tags
  text = text.replace(/<br\s*\/?>/g, '\n');
  
  // Remove any remaining HTML tags
  text = text.replace(/<[^>]*>/g, '');
  
  // Clean up multiple newlines
  text = text.replace(/\n\n+/g, '\n\n');
  
  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&#39;/g, "'");
  
  return text.trim();
};

const AITestingForm = ({ selectedProject, selectedModel, userInfo }) => {
  const { authState } = useOktaAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result] = useState(null);
  const [issueTypes, setIssueTypes] = useState([]);
  const [selectedIssueType, setSelectedIssueType] = useState('');
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueFilter, setIssueFilter] = useState('');
  const [issueDetailsLoading, setIssueDetailsLoading] = useState(false);
  const [issueDetails, setIssueDetails] = useState(null);
  const [testCaseLoading, setTestCaseLoading] = useState(false);
  const [testCase, setTestCase] = useState(null);
  
  // Product Discovery specific states
  const [projectType, setProjectType] = useState(null);
  const [views, setViews] = useState([]);
  const [selectedView, setSelectedView] = useState(null);
  const [viewIdeas, setViewIdeas] = useState({});
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [expandedViews, setExpandedViews] = useState({});

  // Fetch issue types when project changes
  useEffect(() => {
    if (selectedProject && authState?.accessToken) {
      setLoading(true);
      setError(null);
      setSelectedIssueType('');
      setIssues([]);
      setSelectedIssue(null);
      setViews([]);
      setSelectedView(null);
      setViewIdeas({});
      setExpandedViews({});
      
      const token = typeof authState.accessToken === 'string' 
        ? authState.accessToken 
        : authState.accessToken?.accessToken;
      
      // First, fetch project details to determine type
      fetch(`/api/projects/${selectedProject}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.project) {
            setProjectType(data.project.projectTypeKey);
            
            // If it's product discovery, fetch views instead of issue types
            if (data.project.projectTypeKey === 'product-discovery') {
              fetchProductDiscoveryViews(token, selectedProject);
            } else {
              fetchIssueTypes(token, selectedProject);
            }
          } else {
            // Fallback to issue types if project details not available
            fetchIssueTypes(token, selectedProject);
          }
        })
        .catch(err => {
          console.error('Error fetching project details:', err);
          // Fallback to issue types
          fetchIssueTypes(token, selectedProject);
        });
    }
  }, [selectedProject, authState]);

  // Fetch views for product discovery projects
  const fetchProductDiscoveryViews = (token, projectKey) => {
    fetch(`/api/views/${projectKey}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setViews(data.views || []);
          console.log(`[TESTING-FORM] Loaded ${data.views?.length || 0} product discovery views`);
        } else {
          setError(data.error || 'Failed to fetch views');
        }
      })
      .catch(err => {
        setError(err.message || 'Error fetching views');
        console.error('[TESTING-FORM] Error:', err);
      })
      .finally(() => setLoading(false));
  };

  // Fetch standard issue types
  const fetchIssueTypes = (token, projectKey) => {
    fetch(`/api/issue-types/${projectKey}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setIssueTypes(data.issueTypes || []);
        } else {
          setError(data.error || 'Failed to fetch issue types');
        }
      })
      .catch(err => {
        setError(err.message || 'Error fetching issue types');
        console.error('[TESTING-FORM] Error:', err);
      })
      .finally(() => setLoading(false));
  };

  // Fetch ideas for selected view (Product Discovery)
  const fetchIdeasForView = (viewId) => {
    if (!authState?.accessToken) return;
    
    setIdeasLoading(true);
    const token = typeof authState.accessToken === 'string' 
      ? authState.accessToken 
      : authState.accessToken?.accessToken;
    
    fetch(`/api/views/${selectedProject}/${viewId}/ideas`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setViewIdeas(prev => ({
            ...prev,
            [viewId]: data.ideas || []
          }));
          console.log(`[TESTING-FORM] Loaded ${data.ideas?.length || 0} ideas for view ${viewId}`);
        } else {
          setError(data.error || 'Failed to fetch ideas');
        }
      })
      .catch(err => {
        setError(err.message || 'Error fetching ideas');
        console.error('[TESTING-FORM] Error:', err);
      })
      .finally(() => setIdeasLoading(false));
  };

  // Fetch issues when issue type changes
  useEffect(() => {
    if (selectedProject && selectedIssueType && authState?.accessToken) {
      setIssuesLoading(true);
      setError(null);
      setSelectedIssue(null);
      setIssueFilter('');
      
      const token = typeof authState.accessToken === 'string' 
        ? authState.accessToken 
        : authState.accessToken?.accessToken;
      
      // Get the issue type name from the selected ID
      const issueTypeName = issueTypes.find(t => t.id === selectedIssueType)?.name;
      
      if (!issueTypeName) {
        setError('Could not determine issue type name');
        setIssuesLoading(false);
        return;
      }
      
      fetch(`/api/issues-by-type/${selectedProject}/${encodeURIComponent(issueTypeName)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(async res => {
          if (!res.ok) {
            const text = await res.text();
            console.error(`[TESTING-FORM] HTTP ${res.status}: ${text.substring(0, 200)}`);
            throw new Error(`HTTP ${res.status}: Failed to fetch issues`);
          }
          return res.json();
        })
        .then(data => {
          if (data.success) {
            setIssues(data.issues || []);
            setFilteredIssues(data.issues || []);
            console.log(`[TESTING-FORM] Loaded ${data.total} ${issueTypeName} issues`);
          } else {
            setError(data.error || 'Failed to fetch issues');
          }
        })
        .catch(err => {
          setError(err.message || 'Error fetching issues');
          console.error('[TESTING-FORM] Error:', err);
        })
        .finally(() => setIssuesLoading(false));
    }
  }, [selectedProject, selectedIssueType, authState, issueTypes]);

  // Filter issues based on search input
  useEffect(() => {
    if (!issueFilter.trim()) {
      setFilteredIssues(issues);
    } else {
      const searchTerm = issueFilter.toLowerCase();
      const filtered = issues.filter(issue => 
        issue.key.toLowerCase().includes(searchTerm) || 
        issue.summary.toLowerCase().includes(searchTerm)
      );
      setFilteredIssues(filtered);
    }
  }, [issueFilter, issues]);

  // Fetch issue details when issue is selected
  useEffect(() => {
    if (selectedIssue && authState?.accessToken) {
      setIssueDetailsLoading(true);
      setError(null);

      const token = typeof authState.accessToken === 'string' 
        ? authState.accessToken 
        : authState.accessToken?.accessToken;

      fetch(`/api/issue/${selectedIssue.key}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(async res => {
          if (!res.ok) {
            const text = await res.text();
            console.error(`[TESTING-FORM] HTTP ${res.status}: ${text.substring(0, 200)}`);
            throw new Error(`HTTP ${res.status}: Failed to fetch issue details`);
          }
          return res.json();
        })
        .then(data => {
          if (data.success && data.issue) {
            setIssueDetails(data.issue);
            console.log(`[TESTING-FORM] Loaded details for ${selectedIssue.key}`);
          } else {
            setError(data.error || 'Failed to fetch issue details');
          }
        })
        .catch(err => {
          setError(err.message || 'Error fetching issue details');
          console.error('[TESTING-FORM] Error:', err);
        })
        .finally(() => setIssueDetailsLoading(false));
    }
  }, [selectedIssue, authState]);

  // Generate test case when user clicks button
  const generateTestCase = async () => {
    if (!selectedIssue || !issueDetails) {
      setError('Please select a ticket first');
      return;
    }

    setTestCaseLoading(true);
    setError(null);
    
    try {
      const token = typeof authState.accessToken === 'string' 
        ? authState.accessToken 
        : authState.accessToken?.accessToken;

      const response = await fetch('/api/generate-test-case', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          issueKey: selectedIssue.key,
          issueDetails: issueDetails.fields,
          selectedModel: selectedModel || 'gemini-2.5-flash'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setTestCase(data.testCase);
        console.log('[TESTING-FORM] Test case generated successfully');
      } else {
        setError(data.error || 'Failed to generate test case');
      }
    } catch (err) {
      setError(err.message || 'Error generating test case');
      console.error('[TESTING-FORM] Error:', err);
    } finally {
      setTestCaseLoading(false);
    }
  };

  // Update test case field
  const updateTestCaseField = (field, value) => {
    setTestCase(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update test step
  const updateTestStep = (stepIndex, stepField, value) => {
    setTestCase(prev => ({
      ...prev,
      steps: prev.steps.map((step, idx) => 
        idx === stepIndex ? { ...step, [stepField]: value } : step
      )
    }));
  };

  // Add new test step
  const addTestStep = () => {
    setTestCase(prev => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          stepNumber: (prev.steps.length || 0) + 1,
          action: '',
          expectedResult: '',
          notes: ''
        }
      ]
    }));
  };

  // Delete test step
  const deleteTestStep = (stepIndex) => {
    setTestCase(prev => ({
      ...prev,
      steps: prev.steps.filter((_, idx) => idx !== stepIndex).map((step, idx) => ({
        ...step,
        stepNumber: idx + 1
      }))
    }));
  };

  // Export test case as JSON
  const exportTestCase = () => {
    const dataStr = JSON.stringify(testCase, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `test-case-${testCase.issueKey || 'export'}.json`;
    link.click();
  };

  const exportTestCaseAsCSV = () => {
    if (!testCase) return;

    // Helper function to escape CSV fields
    const escapeCSVField = (field) => {
      if (field === null || field === undefined) return '';
      const str = String(field);
      // If field contains comma, quote, or newline, wrap in quotes and escape internal quotes
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Build CSV content with proper tabular format
    let csvContent = '';
    
    // Add headers: ID,Name,Description,Preconditions,Step #,Action,Expected Result,Notes
    const headers = ['ID', 'Name', 'Description', 'Preconditions', 'Step #', 'Action', 'Expected Result', 'Notes'];
    csvContent += headers.map(h => escapeCSVField(h)).join(',') + '\n';
    
    // Format preconditions as a single multi-line string
    const preconditionsText = (testCase.preConditions || [])
      .map(cond => `• ${cond}`)
      .join('\n');
    
    // Create a row for each test step
    // First row includes all test case metadata + first step
    // Subsequent rows repeat test case metadata but leave Name/Description/Preconditions empty
    const steps = testCase.steps || [];
    
    steps.forEach((step, idx) => {
      const row = [
        escapeCSVField('100'), // ID - use a placeholder ID
        idx === 0 ? escapeCSVField(testCase.name) : '', // Name only on first step
        idx === 0 ? escapeCSVField(testCase.description || '') : '', // Description only on first step
        idx === 0 ? escapeCSVField(preconditionsText) : '', // Preconditions only on first step
        escapeCSVField(step.stepNumber || (idx + 1)),
        escapeCSVField(step.action || ''),
        escapeCSVField(step.expectedResult || ''),
        escapeCSVField(step.notes || '')
      ];
      csvContent += row.join(',') + '\n';
    });
    
    // If no steps, add at least one row with the test case info
    if (steps.length === 0) {
      const row = [
        escapeCSVField('100'),
        escapeCSVField(testCase.name),
        escapeCSVField(testCase.description || ''),
        escapeCSVField(preconditionsText),
        '',
        '',
        '',
        ''
      ];
      csvContent += row.join(',') + '\n';
    }

    // Create and download file
    const dataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', `test-case-${testCase.issueKey || 'export'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add a new pre-condition
  const addPreCondition = () => {
    const newPreconditions = [...(testCase.preConditions || []), ''];
    updateTestCaseField('preConditions', newPreconditions);
  };

  // Remove a pre-condition by index
  const removePreCondition = (idx) => {
    const newPreconditions = (testCase.preConditions || []).filter((_, i) => i !== idx);
    updateTestCaseField('preConditions', newPreconditions);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🧪 Testing Flow</h1>
        <p style={styles.subtitle}>Design and execute your testing strategy</p>
      </div>

      {!selectedProject && (
        <div style={styles.warning}>
          ⚠️ Please select a project from the Project Selector above to begin.
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.stepIndicator}>
        <div style={{...styles.stepDot, ...( currentStep === 1 ? styles.stepDotActive : {})}}>1</div>
        <div style={{...styles.stepLine, ...(currentStep >= 2 ? styles.stepLineActive : {})}}></div>
        <div style={{...styles.stepDot, ...(currentStep === 2 ? styles.stepDotActive : {})}}>2</div>
        <div style={{...styles.stepLine, ...(currentStep >= 3 ? styles.stepLineActive : {})}}></div>
        <div style={{...styles.stepDot, ...(currentStep === 3 ? styles.stepDotActive : {})}}>3</div>
      </div>

      {selectedProject && (
        <>
          {currentStep === 1 && (
            <div style={styles.stepContent}>
              <h2>Step 1: Select Test Scope</h2>
              <p style={styles.stepDescription}>What will you be testing?</p>
              
              {/* PRODUCT DISCOVERY VIEW */}
              {projectType === 'product-discovery' ? (
                <>
                  {loading ? (
                    <p style={styles.loadingText}>Loading product discovery views...</p>
                  ) : views.length > 0 ? (
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>
                        📁 Views & Ideas:
                        <p style={styles.hintText}>Select a view below to see its ideas</p>
                      </label>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {views.map((view) => (
                          <div key={view.id} style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                            {/* View Header - Expandable */}
                            <div
                              onClick={() => {
                                setExpandedViews(prev => ({
                                  ...prev,
                                  [view.id]: !prev[view.id]
                                }));
                                // Load ideas if not already loaded
                                if (!viewIdeas[view.id]) {
                                  fetchIdeasForView(view.id);
                                }
                              }}
                              style={{
                                padding: '1rem',
                                backgroundColor: selectedView?.id === view.id ? '#e3f2fd' : '#f8f9fa',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                borderBottom: expandedViews[view.id] ? '1px solid #ddd' : 'none',
                                transition: 'all 0.2s'
                              }}
                            >
                              <span style={{ fontSize: '1.2rem' }}>
                                {expandedViews[view.id] ? '▼' : '▶'}
                              </span>
                              <div style={{ flex: 1 }}>
                                <strong style={{ color: selectedView?.id === view.id ? '#0052cc' : '#333' }}>
                                  {view.name}
                                </strong>
                                {view.description && (
                                  <p style={{ fontSize: '0.9rem', color: '#666', margin: '0.25rem 0 0 0' }}>
                                    {view.description}
                                  </p>
                                )}
                              </div>
                              {viewIdeas[view.id] && (
                                <span style={{ 
                                  backgroundColor: '#0052cc', 
                                  color: '#fff', 
                                  padding: '0.25rem 0.5rem', 
                                  borderRadius: '12px',
                                  fontSize: '0.85rem',
                                  fontWeight: 'bold'
                                }}>
                                  {viewIdeas[view.id].length} ideas
                                </span>
                              )}
                            </div>

                            {/* Ideas List - Expanded */}
                            {expandedViews[view.id] && (
                              <div style={{ backgroundColor: '#fff', padding: '0.75rem' }}>
                                {ideasLoading && !viewIdeas[view.id] ? (
                                  <p style={styles.loadingText}>Loading ideas...</p>
                                ) : viewIdeas[view.id] && viewIdeas[view.id].length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {viewIdeas[view.id].map((idea) => (
                                      <div
                                        key={idea.id}
                                        onClick={() => setSelectedIssue(idea)}
                                        style={{
                                          padding: '0.75rem',
                                          backgroundColor: selectedIssue?.id === idea.id ? '#e8f5e9' : '#f8f9fa',
                                          border: selectedIssue?.id === idea.id ? '2px solid #4caf50' : '1px solid #e9ecef',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          transition: 'all 0.2s'
                                        }}
                                      >
                                        <div style={{ fontWeight: '500', color: '#333' }}>
                                          💡 {idea.name || idea.key}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                                          {idea.summary || idea.description}
                                        </div>
                                        {idea.status && (
                                          <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                                            Status: <strong>{idea.status}</strong>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p style={styles.emptyState}>No ideas in this view</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {selectedIssue && (
                        <div style={styles.selectionInfo}>
                          <p>✅ Ready to plan tests for: <strong>💡 {selectedIssue.name || selectedIssue.key}</strong></p>
                        </div>
                      )}

                      <button 
                        style={{...styles.button, ...styles.buttonPrimary}}
                        onClick={() => setCurrentStep(2)}
                        disabled={!selectedIssue || loading}
                      >
                        Next: Test Planning →
                      </button>
                    </div>
                  ) : (
                    <div style={styles.emptyState}>
                      <p>No views found in this product discovery project.</p>
                      <p style={styles.hintText}>Please select a project with configured views.</p>
                    </div>
                  )}
                </>
              ) : (
                /* STANDARD ISSUE TYPE VIEW */
                <>
                  {loading ? (
                    <p style={styles.loadingText}>Loading issue types...</p>
                  ) : issueTypes.length > 0 ? (
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>
                        Issue Type:
                        <select 
                          value={selectedIssueType} 
                          onChange={(e) => setSelectedIssueType(e.target.value)}
                          style={styles.select}
                          disabled={loading}
                        >
                          <option value="">-- Select an issue type to test --</option>
                          {issueTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                              {type.description ? ` - ${type.description}` : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                      
                      {selectedIssueType && (
                        <>
                          <div style={styles.selectionInfo}>
                            <p>✅ Selected: <strong>{issueTypes.find(t => t.id === selectedIssueType)?.name}</strong></p>
                          </div>

                          {issuesLoading ? (
                            <div style={styles.formGroup}>
                              <p style={styles.loadingText}>Loading all {issueTypes.find(t => t.id === selectedIssueType)?.name} issues...</p>
                            </div>
                          ) : issues.length > 0 ? (
                            <div style={styles.formGroup}>
                              <label style={styles.formLabel}>
                                Search Issues:
                                <input 
                                  type="text"
                                  placeholder={`Search by key or summary... (${filteredIssues.length} of ${issues.length})`}
                                  value={issueFilter}
                                  onChange={(e) => setIssueFilter(e.target.value)}
                                  style={styles.searchInput}
                                />
                              </label>

                              <label style={styles.formLabel}>
                                Select Issue:
                                <select 
                                  value={selectedIssue ? selectedIssue.key : ''} 
                                  onChange={(e) => {
                                    const key = e.target.value;
                                    const issue = issues.find(i => i.key === key);
                                    setSelectedIssue(issue || null);
                                  }}
                                  style={styles.select}
                                  disabled={issuesLoading}
                                >
                                  <option value="">-- Select a specific issue to test --</option>
                                  {filteredIssues.map((issue) => (
                                    <option key={issue.key} value={issue.key}>
                                      {issue.key} - {issue.summary} ({issue.status})
                                    </option>
                                  ))}
                                </select>
                              </label>
                              
                              <div style={styles.issueCount}>
                                📊 {filteredIssues.length} of {issues.length} {issueTypes.find(t => t.id === selectedIssueType)?.name} issue(s) matching filter
                              </div>

                              {selectedIssue && (
                                <div style={styles.selectionInfo}>
                                  <p>✅ Ready to plan tests for: <strong>{selectedIssue.key}</strong> - {selectedIssue.summary}</p>
                                  <p style={styles.statusBadge}>Status: <strong>{selectedIssue.status}</strong></p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={styles.emptyState}>
                              <p>No {issueTypes.find(t => t.id === selectedIssueType)?.name} issues found in this project.</p>
                              <p style={styles.hintText}>Try selecting a different issue type.</p>
                            </div>
                          )}
                        </>
                      )}
                      
                      <button 
                        style={{...styles.button, ...styles.buttonPrimary}}
                        onClick={() => setCurrentStep(2)}
                        disabled={!selectedIssue || loading || issuesLoading}
                      >
                        Next: Test Planning →
                      </button>
                    </div>
                  ) : (
                    <div style={styles.emptyState}>
                      <p>No issue types found for this project.</p>
                      <p style={styles.hintText}>Please select a project with issue types configured.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div style={styles.stepContent}>
              <h2>Step 2: Review Test Scope</h2>
              <p style={styles.stepDescription}>View the ticket details in read-only mode</p>
              
              {issueDetailsLoading ? (
                <p style={styles.loadingText}>Loading ticket details...</p>
              ) : issueDetails ? (
                <div style={styles.ticketViewContainer}>
                  {/* Ticket Header */}
                  <div style={styles.ticketHeader}>
                    <div style={styles.ticketTitle}>
                      <span style={{ color: '#0052cc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {issueDetails.key}
                      </span>
                      <span style={{ marginLeft: '0.5rem', fontSize: '1.1rem' }}>
                        {issueDetails.fields?.summary}
                      </span>
                    </div>
                    <div style={styles.ticketMeta}>
                      <span style={{ marginRight: '1rem' }}>
                        <strong>Type:</strong> {issueDetails.fields?.issuetype?.name}
                      </span>
                      <span>
                        <strong>Status:</strong> {issueDetails.fields?.status?.name}
                      </span>
                    </div>
                  </div>

                  {/* Description Section */}
                  {issueDetails.fields?.description && (
                    <div style={styles.ticketSection}>
                      <h4 style={styles.sectionTitle}>Description</h4>
                      <div style={styles.descriptionBox}>
                        {formatDescription(issueDetails.fields.description)}
                      </div>
                    </div>
                  )}

                  {/* Additional Details */}
                  <div style={styles.ticketSection}>
                    <h4 style={styles.sectionTitle}>Details</h4>
                    <div style={styles.detailsGrid}>
                      {issueDetails.fields?.priority && (
                        <div style={styles.detailItem}>
                          <strong>Priority:</strong> {issueDetails.fields.priority.name}
                        </div>
                      )}
                      {issueDetails.fields?.assignee && (
                        <div style={styles.detailItem}>
                          <strong>Assignee:</strong> {issueDetails.fields.assignee.displayName}
                        </div>
                      )}
                      {issueDetails.fields?.reporter && (
                        <div style={styles.detailItem}>
                          <strong>Reporter:</strong> {issueDetails.fields.reporter.displayName}
                        </div>
                      )}
                      {issueDetails.fields?.created && (
                        <div style={styles.detailItem}>
                          <strong>Created:</strong> {new Date(issueDetails.fields.created).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '1.5rem', fontStyle: 'italic' }}>
                    📌 This ticket view is read-only. Plan your test cases based on the information above.
                  </p>
                </div>
              ) : (
                <div style={styles.emptyState}>
                  <p>Unable to load ticket details</p>
                  <p style={styles.hintText}>Try going back and selecting a different ticket</p>
                </div>
              )}

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', justifyContent: 'space-between' }}>
                <button 
                  style={{...styles.button, ...styles.buttonSecondary}}
                  onClick={() => setCurrentStep(1)}
                >
                  ← Back to Selection
                </button>
                <button 
                  style={{...styles.button, ...styles.buttonPrimary}}
                  onClick={() => setCurrentStep(3)}
                  disabled={!issueDetails || issueDetailsLoading}
                >
                  Next: Test Execution →
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div style={styles.stepContent}>
              <h2>Step 3: Generate & Edit Test Case</h2>
              <p style={styles.stepDescription}>Create a JAMA test case from your ticket</p>
              
              {!testCase ? (
                <div>
                  <p style={styles.infoBox}>
                    Click below to generate a test case with multiple test steps based on the ticket requirements.
                  </p>
                  <button 
                    style={{...styles.button, ...styles.buttonPrimary}}
                    onClick={generateTestCase}
                    disabled={testCaseLoading || !issueDetails}
                  >
                    {testCaseLoading ? '🔄 Generating Test Case...' : '✨ Generate Test Case'}
                  </button>
                </div>
              ) : (
                <div style={styles.testCaseContainer}>
                  {/* Test Case Metadata */}
                  <div style={styles.testCaseSection}>
                    <h3 style={styles.sectionTitle}>Test Case Metadata</h3>
                    
                    <div style={styles.metadataGrid}>
                      <div style={styles.metadataField}>
                        <label><strong>Project ID:</strong></label>
                        <input 
                          type="text"
                          value={testCase.projectId || ''}
                          onChange={(e) => updateTestCaseField('projectId', e.target.value)}
                          style={styles.metadataInput}
                          readOnly
                        />
                      </div>
                      
                      <div style={styles.metadataField}>
                        <label><strong>Global ID:</strong></label>
                        <input 
                          type="text"
                          value={testCase.globalId || ''}
                          onChange={(e) => updateTestCaseField('globalId', e.target.value)}
                          style={styles.metadataInput}
                          readOnly
                        />
                      </div>
                    </div>

                    <div style={styles.metadataField}>
                      <label><strong>Name:</strong></label>
                      <input 
                        type="text"
                        value={testCase.name || ''}
                        onChange={(e) => updateTestCaseField('name', e.target.value)}
                        style={styles.metadataInput}
                      />
                    </div>

                    <div style={styles.metadataField}>
                      <label><strong>Description:</strong></label>
                      <textarea 
                        value={testCase.description || ''}
                        onChange={(e) => updateTestCaseField('description', e.target.value)}
                        style={{...styles.metadataInput, minHeight: '80px'}}
                      />
                    </div>

                    <div style={styles.metadataGrid}>
                      <div style={styles.metadataField}>
                        <label><strong>Test Category:</strong></label>
                        <input 
                          type="text"
                          value={testCase.testCategory || 'Test Case'}
                          onChange={(e) => updateTestCaseField('testCategory', e.target.value)}
                          style={styles.metadataInput}
                        />
                      </div>
                      
                      <div style={styles.metadataField}>
                        <label><strong>Workflow Status:</strong></label>
                        <select 
                          value={testCase.workflowStatus || 'Draft'}
                          onChange={(e) => updateTestCaseField('workflowStatus', e.target.value)}
                          style={styles.metadataInput}
                        >
                          <option>Draft</option>
                          <option>In Review</option>
                          <option>Ready</option>
                          <option>Approved</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Pre-Conditions */}
                  <div style={styles.testCaseSection}>
                    <h3 style={styles.sectionTitle}>Pre-Conditions</h3>
                    <div style={styles.preConditionsList}>
                      {(testCase.preConditions || []).map((condition, idx) => (
                        <div key={idx} style={styles.preConditionItem}>
                          <input 
                            type="text"
                            value={condition}
                            onChange={(e) => {
                              const newPreconditions = [...(testCase.preConditions || [])];
                              newPreconditions[idx] = e.target.value;
                              updateTestCaseField('preConditions', newPreconditions);
                            }}
                            style={styles.preConditionInput}
                          />
                          <button
                            onClick={() => removePreCondition(idx)}
                            style={{...styles.deleteButton}}
                            title="Remove pre-condition"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                    <button 
                      style={{...styles.button, ...styles.buttonSecondary, marginTop: '0.5rem'}}
                      onClick={addPreCondition}
                    >
                      + Add Pre-Condition
                    </button>
                  </div>

                  {/* Test Steps */}
                  <div style={styles.testCaseSection}>
                    <h3 style={styles.sectionTitle}>Test Steps</h3>
                    <div style={styles.stepsTable}>
                      <div style={styles.stepsTableHeader}>
                        <div style={{...styles.stepsTableCell, width: '5%', fontWeight: 'bold'}}>#</div>
                        <div style={{...styles.stepsTableCell, width: '30%', fontWeight: 'bold'}}>Action</div>
                        <div style={{...styles.stepsTableCell, width: '35%', fontWeight: 'bold'}}>Expected Result</div>
                        <div style={{...styles.stepsTableCell, width: '25%', fontWeight: 'bold'}}>Notes</div>
                        <div style={{...styles.stepsTableCell, width: '5%', fontWeight: 'bold'}}>✕</div>
                      </div>

                      {(testCase.steps || []).map((step, idx) => (
                        <div key={idx} style={styles.stepsTableRow}>
                          <div style={{...styles.stepsTableCell, width: '5%', textAlign: 'center'}}>
                            {step.stepNumber}
                          </div>
                          <div style={{...styles.stepsTableCell, width: '30%'}}>
                            <textarea 
                              value={step.action || ''}
                              onChange={(e) => updateTestStep(idx, 'action', e.target.value)}
                              style={styles.stepInput}
                              placeholder="What action to perform"
                            />
                          </div>
                          <div style={{...styles.stepsTableCell, width: '35%'}}>
                            <textarea 
                              value={step.expectedResult || ''}
                              onChange={(e) => updateTestStep(idx, 'expectedResult', e.target.value)}
                              style={styles.stepInput}
                              placeholder="What should happen"
                            />
                          </div>
                          <div style={{...styles.stepsTableCell, width: '25%'}}>
                            <textarea 
                              value={step.notes || ''}
                              onChange={(e) => updateTestStep(idx, 'notes', e.target.value)}
                              style={styles.stepInput}
                              placeholder="Optional notes"
                            />
                          </div>
                          <div style={{...styles.stepsTableCell, width: '5%', textAlign: 'center'}}>
                            <button 
                              style={styles.deleteButton}
                              onClick={() => deleteTestStep(idx)}
                              title="Delete this step"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button 
                      style={{...styles.button, ...styles.buttonSecondary, marginTop: '1rem'}}
                      onClick={addTestStep}
                    >
                      + Add Test Step
                    </button>
                  </div>

                  {/* Export and Navigation */}
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button 
                        style={{...styles.button, ...styles.buttonSecondary}}
                        onClick={() => setTestCase(null)}
                      >
                        ← Generate New
                      </button>
                      <button 
                        style={{...styles.button, ...styles.buttonSecondary}}
                        onClick={() => setCurrentStep(2)}
                      >
                        ← Back to Ticket
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button 
                        style={{...styles.button, ...styles.buttonSuccess}}
                        onClick={exportTestCaseAsCSV}
                      >
                        📊 Export as CSV
                      </button>
                      <button 
                        style={{...styles.button, ...styles.buttonSuccess}}
                        onClick={exportTestCase}
                      >
                        💾 Export as JSON
                      </button>
                    </div>
                  </div>

                  <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '1.5rem', fontStyle: 'italic', backgroundColor: '#e7f3ff', padding: '0.75rem', borderLeft: '4px solid #0052cc' }}>
                    📝 <strong>Tip:</strong> Edit the test case as needed. Click "Export as JSON" to download and prepare for JAMA import. Eventually you'll be able to save directly to JAMA.
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {result && (
        <div style={styles.result}>
          <h3>✅ {result.message || 'Success!'}</h3>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
    backgroundColor: 'transparent',
    borderRadius: '8px',
  },
  header: {
    marginBottom: '2rem',
    textAlign: 'center',
  },
  title: {
    fontSize: '2.5rem',
    color: '#333',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#666',
    margin: 0,
  },
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '2rem',
    gap: '0.5rem',
  },
  stepDot: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#e9ecef',
    border: '2px solid #dee2e6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    color: '#6c757d',
  },
  stepDotActive: {
    backgroundColor: '#6f42c1',
    color: '#fff',
    border: '2px solid #6f42c1',
  },
  stepLine: {
    flex: 1,
    height: '2px',
    backgroundColor: '#dee2e6',
    margin: '0 0.5rem',
  },
  stepLineActive: {
    backgroundColor: '#6f42c1',
  },
  stepContent: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: '2rem',
  },
  stepDescription: {
    fontSize: '1.1rem',
    color: '#666',
    marginBottom: '1.5rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  formLabel: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '0.75rem',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    marginTop: '0.5rem',
    transition: 'border-color 0.2s',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid #2196f3',
    borderRadius: '4px',
    backgroundColor: '#f0f7ff',
    marginTop: '0.5rem',
    transition: 'all 0.2s',
  },
  selectionInfo: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#e8f5e9',
    border: '1px solid #4caf50',
    borderRadius: '4px',
    color: '#2e7d32',
  },
  statusBadge: {
    marginTop: '0.5rem',
    fontSize: '0.9rem',
    fontWeight: '500',
  },
  issueCount: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3',
    borderRadius: '4px',
    color: '#1565c0',
    fontWeight: '500',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    color: '#666',
  },
  loadingText: {
    textAlign: 'center',
    color: '#6c757d',
    fontStyle: 'italic',
  },
  hintText: {
    color: '#999',
    fontSize: '0.9rem',
  },
  warning: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    color: '#856404',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
  },
  error: {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    color: '#721c24',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
  },
  result: {
    backgroundColor: '#d4edda',
    border: '1px solid #c3e6cb',
    color: '#155724',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
  },
  button: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    marginTop: '1rem',
    marginRight: '0.5rem',
  },
  buttonPrimary: {
    backgroundColor: '#6f42c1',
    color: '#fff',
  },
  buttonSecondary: {
    backgroundColor: '#6c757d',
    color: '#fff',
  },
  ticketViewContainer: {
    backgroundColor: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    padding: '1.5rem',
    marginTop: '1rem',
  },
  ticketHeader: {
    paddingBottom: '1rem',
    borderBottom: '2px solid #e9ecef',
    marginBottom: '1.5rem',
  },
  ticketTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  ticketMeta: {
    fontSize: '0.95rem',
    color: '#666',
    marginTop: '0.5rem',
  },
  ticketSection: {
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '0.75rem',
    marginTop: 0,
  },
  descriptionBox: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    padding: '1rem',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    lineHeight: '1.6',
    color: '#333',
    fontSize: '0.95rem',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
  },
  detailItem: {
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    fontSize: '0.9rem',
    color: '#555',
  },
  infoBox: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3',
    borderRadius: '4px',
    padding: '1rem',
    color: '#1565c0',
    marginBottom: '1rem',
  },
  testCaseContainer: {
    backgroundColor: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    padding: '1.5rem',
    marginTop: '1rem',
  },
  testCaseSection: {
    marginBottom: '2rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #e9ecef',
  },
  metadataGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  metadataField: {
    display: 'flex',
    flexDirection: 'column',
  },
  metadataInput: {
    padding: '0.75rem',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    marginTop: '0.5rem',
  },
  preConditionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  preConditionItem: {
    display: 'flex',
    alignItems: 'center',
  },
  preConditionInput: {
    padding: '0.75rem',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    fontSize: '0.95rem',
    flex: 1,
  },
  stepsTable: {
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '1rem',
  },
  stepsTableHeader: {
    display: 'flex',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
    fontWeight: 'bold',
  },
  stepsTableRow: {
    display: 'flex',
    borderBottom: '1px solid #e9ecef',
    padding: 0,
  },
  stepsTableCell: {
    padding: '0.75rem',
    borderRight: '1px solid #e9ecef',
  },
  stepInput: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #dee2e6',
    borderRadius: '3px',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
    minHeight: '60px',
    resize: 'vertical',
  },
  deleteButton: {
    backgroundColor: 'transparent',
    color: '#666',
    border: 'none',
    borderRadius: '3px',
    padding: '0.5rem 0.75rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '1.2rem',
  },
  buttonSuccess: {
    backgroundColor: '#28a745',
    color: '#fff',
  },
};

export default AITestingForm;
